import { AnimationFrames, Context } from '@vpap_svelte/core';
import { extract, isBrowser, isNullish, watch } from '@vpap_svelte/internal';
import { ChartContext } from './ChartDimensions.svelte.js';
import { onDestroy, onMount } from 'svelte';
import { SvelteMap } from 'svelte/reactivity';
/**
 * A layer for managing the canvas
 */
export class CanvasManager {
    #options;
    #raf;
    // the dimensions of the underlying chart
    #dimensions;
    canvas = $derived(extract(this.#options.element, undefined));
    #currentLayerId = -1;
    #layers;
    constructor(options) {
        this.#options = options;
        this.#raf = new AnimationFrames((params) => {
            this.#render.bind(this)(params);
        }, {
            immediate: options.autoPlay,
            fpsLimit: options.fpsLimit,
        });
        this.#dimensions = ChartContext.get();
        this.#layers = new SvelteMap();
        watch([
            () => this.#dimensions.chartDpiDimensions.width,
            () => this.#dimensions.chartDpiDimensions.height,
        ], () => {
            this.#render.bind(this)();
        });
    }
    #render(rafCallback) {
        if (!isBrowser())
            return;
        if (this.context instanceof CanvasRenderingContext2D && this.#options.autoClear) {
            this.context.clearRect(0, 0, this.#dimensions.chartDpiDimensions.width, this.#dimensions.chartDpiDimensions.height);
        }
        for (const opts of this.#layers.values()) {
            if (opts.render) {
                opts.render({
                    context: this.context,
                    dimensions: this.#dimensions,
                    animation: this.#raf,
                    animationParams: rafCallback,
                });
            }
        }
    }
    #getLayerId() {
        return this.#currentLayerId++;
    }
    register({ setup, render, teardown }) {
        const layerId = this.#getLayerId();
        if (setup) {
            onMount(() => {
                setup({ context: this.context, dimensions: this.#dimensions, animation: this.#raf });
            });
        }
        this.#layers.set(layerId, { setup, teardown, render });
        if (teardown) {
            onDestroy(() => {
                teardown({ context: this.context, dimensions: this.#dimensions, animation: this.#raf });
                this.#layers.delete(layerId);
            });
        }
    }
    /**
     * Get the canvas rendering context
     */
    get context() {
        if (!isBrowser() || isNullish(this.canvas)) {
            return undefined;
        }
        const contextType = this.#options.contextType ?? '2d';
        if (contextType === '2d') {
            return this.canvas.getContext('2d') ?? undefined;
        }
        // webgl2 is technically different from the others but is almost entirely backwards
        // compatible with webgl. For simplicity, we'll simply use 1 interface to handle all of these contexts
        const possibleGlContexts = ['webgl2', 'webgl', 'experimental-webgl', 'moz-webgl', 'webkit-3d'];
        for (const possibleContext of possibleGlContexts) {
            const ctx = this.canvas.getContext(possibleContext, this.#options.contextSettings);
            if (!isNullish(ctx)) {
                return ctx;
            }
        }
        return undefined;
    }
}
export class SvgManager {
    #options;
    // a registry for things that go inside the defs element
    #defItemRegistry;
    element = $derived(extract(this.#options.element, undefined));
    defsElement = $derived(extract(this.#options.defs, undefined));
    isSsr = $derived(extract(this.#options.ssr, false));
    constructor(opts) {
        this.#options = opts;
        this.#defItemRegistry = new SvelteMap();
    }
    registerPattern(id, content) {
        this.#defItemRegistry.set(id, content);
        onDestroy(() => {
            this.#defItemRegistry.delete(id);
        });
    }
    get patterns() {
        return this.#defItemRegistry;
    }
    get pattern() {
        return (id) => {
            return this.#defItemRegistry.get(id);
        };
    }
    get patternUrl() {
        function url(id) {
            return `url(#${id})`;
        }
        return url.bind(this);
    }
    get patternHref() {
        function href(id) {
            return `#${id}`;
        }
        return href.bind(this);
    }
}
/**
 * A very simple manager for the HTML layer.
 */
export class HtmlManager {
    #options;
    element = $derived(extract(this.#options.element, undefined));
    isSsr = $derived(extract(this.#options.ssr, false));
    constructor(opts) {
        this.#options = opts;
    }
}
export const ActiveLayer = new Context('ActiveChartLayer');
